<template>
  <page-template page-id="add-mobile-page" ref="super">

    <template v-slot:before v-if="isTitlebarActive">
      <titlebar :text="$t('pages.setMobile.title')"></titlebar>
    </template>

    <div class="ui column">

      <brand-header :text="$t('pages.setMobile.header')"
                    :has-extension="hasHeaderExtension"
                    :has-logo="hasBrandLogo">
      </brand-header>

      <p v-html="$t('pages.setMobile.subheader')"></p>

      <messages id="notifications" ref="postman"></messages>

      <validated-field :show-errors-on="showErrors" :has-any-errors="$v.newMobile.$invalid"
                       id="mobile-errors"
                       :validation-rules="[
                             {value: $v.newMobile.required, msg: $t('common.validation.username.mobileEmpty')},
                             {value: $v.newMobile.validMobile, showOn: $v.newMobile.required,
                                msg: $t('common.validation.username.mobileFormat')}]">
        <labeled-input-field id="mobile" v-model="$v.newMobile.$model"
                             autocomplete="tel"
                             :label="$t('common.label.newMobile')"
                             @enter-pressed.prevent="onSubmit"
                             @input="onMobileInput"
                             @click.right.prevent @copy.prevent @paste.prevent
                             aria-error-element-id="mobile-errors notifications"
                             :mandatory="true"
                             :invalid="$v.newMobile.$invalid && showErrors"></labeled-input-field>
      </validated-field>

      <validated-field :show-errors-on="showErrors" :has-any-errors="$v.confirmMobile.$invalid"
                       id="confirmMobile-errors"
                       :validation-rules="[
                               {value: $v.confirmMobile.required, msg: $t('common.validation.username.confirmMobileEmpty')},
                               {value: $v.confirmMobile.validMobile, showOn: $v.confirmMobile.required, msg: $t('common.validation.username.mobileFormat')},
                               {value: $v.confirmMobile.sameAsMobile, showOn: $v.confirmMobile.validMobile, msg: $t('common.validation.username.mismatchedMobile') }]">
        <labeled-input-field id="confirmMobile" v-model="$v.confirmMobile.$model" ref="confirmMobile"
                             :label="$t('common.label.confirmMobile')"
                             @enter-pressed.prevent="onSubmit"
                             @input="onConfirmMobileInput"
                             @click.right.prevent @copy.prevent @paste.prevent
                             aria-error-element-id="mobile-errors notifications"
                             :mandatory="true"
                             :invalid="$v.confirmMobile.$invalid && showErrors"></labeled-input-field>
      </validated-field>
      <validated-field id="password-errors"
                       :show-errors-on="showErrors" :has-any-errors="$v.password.$invalid"
                       :validation-rules="[{value: $v.password.required, msg: this.$t('common.validation.field.empty')}]">
        <password-input-field id="password" v-model="$v.password.$model"
                              autocomplete="current-password"
                              :label="$t('common.label.password')"
                              :invalid="$v.password.$invalid && showErrors"
                              :mandatory="true"
                              aria-error-element-id="password-errors server-errors"
                              @enter-pressed.prevent="onSubmit"></password-input-field>
      </validated-field>

      <div class="field">
        <button id="saveChange" type="submit" v-text="$t('pages.setMobile.button')"
                class="ui fluid primary button" @click.prevent="onSubmit"></button>
      </div>

      <div class="field">
        <button id="back" type="button" v-text="$t('common.button.cancel')"
                class="ui fluid basic button" @click="goBack"></button>
      </div>
    </div>
  </page-template>
</template>

<script>
import PageTemplate from './template/PageTemplate.vue'
import Titlebar from '../components/Titlebar.vue'
import Messages from '../components/Messages.vue'
import LabeledInputField from '../components/LabeledInputField.vue'
import ValidatedField from '../components/ValidatedField.vue'
import { mixin } from '../js/ciam-mixin.js'
import { brand } from '../js/brand.js'
import { focusOn } from '../js/helpers.js'
import { required, sameAs } from 'vuelidate/lib/validators'
import { AsYouType, getCountryCallingCode } from 'libphonenumber-js/mobile'
import BrandHeader from '../components/BrandHeader.vue'
import PasswordInputField from '../components/LabeledPasswordInputField.vue'

export default {
  components: {
    PasswordInputField,
    BrandHeader,
    PageTemplate,
    Titlebar,
    Messages,
    LabeledInputField,
    ValidatedField
  },
  mixins: [mixin, brand],
  props: {
    unconfirmedUsername: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      uid: this.$store.state.uid || '',
      username: sessionStorage.getItem('storedusername') || this.$store.state.username,
      newMobile: this.isMobile(this.unconfirmedUsername || this.$store.state.unconfirmedUsername) ? this.unconfirmedUsername || this.$store.state.unconfirmedUsername : '',
      confirmMobile: '',
      codeEndpointBase: '/code',
      codeEndpointPath: '/username/init',
      showErrors: false,
      isValidMobile: false,
      isValidConfirmMobile: false,
      password: '',
      matchedMobile: false
    }
  },
  validations () {
    return {
      newMobile: {
        required,
        validMobile () {
          return this.isValidMobile
        }
      },
      confirmMobile: {
        required,
        validMobile () {
          return this.isValidConfirmMobile
        },
        sameAsMobile: sameAs('newMobile')
      },
      password: { required }
    }
  },
  methods: {
    onSubmit: function () {
      this.$refs.postman.clear()
      this.showErrors = false

      if (!this.isFormValid()) return
      this.$refs.super.startLoading()
      const payload = this.$api.payloads.buildChangeUsernamePayload(this.username, this.newMobile, this.password)
      this.$api.login.setMobile(payload, this.handleRoutingResponse, this.onFail)
    },
    onFail: function (resp) {
      this.onAjaxError(resp)
      this.$refs.super.stopLoading()
      focusOn('newMobile')
    },
    onMobileInput: function () {
      if (this.newMobile) {
        const asYouType = new AsYouType()
        this.newMobile = asYouType.input(this.newMobile.replace(/[^0-9+]/g, ''))
        this.isValidMobile = asYouType.isValid()
        $('#mobile').val(this.newMobile)
      } else {
        this.isValidMobile = false
      }
    },
    isFormValid: function () {
      if (!this.$v.$anyDirty || this.$v.$anyError || this.$v.$invalid) {
        const _this = this

        this.focusFirstErrorField(function () {
          _this.showErrors = true
        })

        return false
      }

      return true
    },
    onConfirmMobileInput: function () {
      if (this.confirmMobile) {
        console.log(`typing confirm mobile: ${this.confirmMobile}`)
        const asYouType = new AsYouType()
        this.confirmMobile = asYouType.input(this.confirmMobile.replace(/[^0-9+]/g, ''))

        this.isValidConfirmMobile = asYouType.isValid()
        this.matchedMobile = this.newMobile === this.confirmMobile

        if (!this.matchedMobile) {
          this.showErrors = true
        }
        $('#confirmMobile').val(this.confirmMobile)
      } else {
        this.isValidConfirmMobile = false
      }
    },
    fillCountryCode: function () {
      this.unconfirmedUsername = this.unconfirmedUsername || this.$store.state.unconfirmedUsername
      if (this.unconfirmedUsername) {
        // prefill
        const asYouType = new AsYouType()
        this.newMobile = asYouType.input(this.unconfirmedUsername.replace(/[^0-9+]/g, ''))
        this.isValidMobile = asYouType.isValid()
      } else {
        const countryCode = getCountryCallingCode(this.$store.state.country)
        if (countryCode) {
          this.newMobile = '+' + countryCode
        }
      }
    },
    checkHideErrors: function () {
      if (this.$v.$invalid) return
      this.showErrors = false
    }
  },
  computed: {
  },
  mounted: function () {
    console.info(`Set mobile [uid=${this.uid}, username=${this.username}, codeSent=${this.codeSent}, hasPass=${this.hasPass}]`)
    this.fillCountryCode()
    focusOn('newMobile')
  },
  watch: {
    $v: {
      handler () {
        this.checkHideErrors()
      },
      deep: true
    }
  }
}
</script>
<style>
</style>
